import React from 'react';

const tips = [
  {
    number: '01',
    title: 'Pitching: same motion, variable speed.',
    cue: 'Cue: chest, hinge, core.',
    body: 'Use one repeatable pitching motion and adjust distance by changing speed, not by changing the basic mechanics. Keep the body involved through the chest and core, with a natural hinge instead of a handsy flip.',
  },
  {
    number: '02',
    title: 'Stand taller and set the club correctly.',
    cue: 'Cue: shaft points toward the belt buckle.',
    body: 'Do not let the hands get too low. At address, the heel should sit on the floor with the toe slightly up, and the ball should feel centered off the feet rather than heel- or toe-biased.',
  },
  {
    number: '03',
    title: 'Confirm the face is truly square.',
    cue: 'Cue: correct square may feel slightly closed.',
    body: 'Your old “square” feel may have been open. Check the face before blaming the path. Keep the grip secure but not tight.',
  },
  {
    number: '04',
    title: 'Stay balanced on the balls of your feet.',
    cue: 'Cue: steady body, stable head.',
    body: 'Keep the head stable in the backswing, downswing, and through impact. Do not pick up early to look for the ball.',
  },
  {
    number: '05',
    title: 'Rotate the chest instead of dipping the shoulders.',
    cue: 'Cue: chest turns horizontally toward the right toe.',
    body: 'A shoulder-only turn tends to point down toward the left toe and create a reverse pivot. Keep the arm triangle and let the chest start the motion.',
  },
  {
    number: '06',
    title: 'Load into the right side in the backswing.',
    cue: 'Cue: feel abs and right thigh.',
    body: 'Avoid collapsing into the left side. The backswing should store pressure and structure on the right side without swaying or moving forward.',
  },
  {
    number: '07',
    title: 'Let the wrists hinge naturally.',
    cue: 'Cue: loose, movable hands.',
    body: 'Do not force the hinge on purpose, and do not exaggerate it once it starts working. The hinge should appear from the motion.',
  },
  {
    number: '08',
    title: 'Start down with the left foot and left glute.',
    cue: 'Cue: lower body starts, everything follows.',
    body: 'Activate the left side, let it pull out of the way, then rotate the hips around. Do not turn it into a hips-only swing; the upper body must keep rotating too.',
  },
  {
    number: '09',
    title: 'Keep the arms connected to the chest.',
    cue: 'Cue: keep the triangle through and across.',
    body: 'The arms should travel with the chest instead of disconnecting. Rotate through the ball while the hands stay loose and free.',
  },
  {
    number: '10',
    title: 'Choose smoothness over effort.',
    cue: 'Cue: steady through the ball.',
    body: 'When contact gets inconsistent, reduce power first. Timing on the way down is more important than speed or effort.',
  },
];

const resetSteps = [
  'Check setup height, shaft-to-belt-buckle line, and square face.',
  'Make slow chest-rotation rehearsals: horizontal toward the right toe, not dipping toward the left toe.',
  'Feel pressure load into the right side and right thigh.',
  'Start down by activating the left foot and letting the hips rotate around.',
  'Keep wrists loose and let the hinge happen naturally.',
  'Hit half swings until solid contact returns.',
  'Return to full swing only after center-face contact is back.',
];

export default function GolfTips() {
  return (
    <div className="golf-tips-page">
      <style>{`
        .golf-tips-page {
          --bg: #f6f4ef;
          --card: #ffffff;
          --ink: #1f2933;
          --muted: #5f6b76;
          --green: #1f7a4d;
          --green-dark: #155a38;
          --gold: #c99a2e;
          --line: #d8d2c3;
          margin: -2rem -2.5rem;
          min-height: 100vh;
          font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          background:
            radial-gradient(circle at top left, rgba(31, 122, 77, 0.12), transparent 32rem),
            linear-gradient(180deg, #fbfaf6 0%, var(--bg) 100%);
          color: var(--ink);
          line-height: 1.55;
        }

        .golf-tips-page * { box-sizing: border-box; }

        .golf-tips-main { max-width: 1120px; margin: 0 auto; padding: 48px 20px 72px; }

        .golf-tips-page .hero { display: grid; grid-template-columns: 1.4fr 0.8fr; gap: 28px; align-items: stretch; margin-bottom: 32px; }
        .golf-tips-page .hero-card, .golf-tips-page .update-card, .golf-tips-page .tip, .golf-tips-page .practice-card { background: rgba(255,255,255,0.88); border: 1px solid var(--line); border-radius: 24px; box-shadow: 0 16px 44px rgba(31,41,51,0.08); }
        .golf-tips-page .hero-card { padding: 34px; }
        .golf-tips-page .eyebrow { color: var(--green); font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; font-size: 0.78rem; margin: 0 0 10px; }
        .golf-tips-page h1 { font-size: clamp(2.2rem, 5vw, 4.4rem); line-height: 0.95; margin: 0 0 18px; letter-spacing: -0.06em; color: var(--ink); }
        .golf-tips-page .dek { color: var(--muted); font-size: 1.08rem; max-width: 62ch; margin: 0; }
        .golf-tips-page .update-card { padding: 26px; display: flex; flex-direction: column; justify-content: space-between; background: linear-gradient(145deg, var(--green), var(--green-dark)); color: white; border-color: rgba(255,255,255,0.18); }
        .golf-tips-page .update-card p { margin: 0; color: rgba(255,255,255,0.84); }
        .golf-tips-page .version { display: inline-flex; width: fit-content; border: 1px solid rgba(255,255,255,0.35); border-radius: 999px; padding: 7px 12px; font-size: 0.84rem; font-weight: 700; margin-bottom: 18px; }
        .golf-tips-page .focus { margin-top: 24px; padding-top: 18px; border-top: 1px solid rgba(255,255,255,0.22); font-weight: 800; font-size: 1.2rem; color: white; }
        .golf-tips-page .priority-list { margin: 10px 0 0; padding-left: 1.2rem; font-weight: 700; font-size: 1rem; color: rgba(255,255,255,0.9); }
        .golf-tips-page .priority-list li { margin: 7px 0; }
        .golf-tips-page .tips-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; margin: 28px 0; }
        .golf-tips-page .tip { padding: 22px; position: relative; overflow: hidden; }
        .golf-tips-page .tip::before { content: ""; position: absolute; inset: 0 auto 0 0; width: 6px; background: linear-gradient(var(--green), var(--gold)); }
        .golf-tips-page .tip-header { display: flex; align-items: baseline; gap: 12px; margin-bottom: 8px; }
        .golf-tips-page .num { color: var(--gold); font-weight: 900; font-size: 1.45rem; line-height: 1; min-width: 2.2rem; }
        .golf-tips-page h2 { font-size: 1.16rem; line-height: 1.2; margin: 0; letter-spacing: -0.02em; color: var(--ink); }
        .golf-tips-page .cue { color: var(--green-dark); font-weight: 800; margin: 10px 0 8px; }
        .golf-tips-page .tip p { margin: 0; color: var(--muted); }
        .golf-tips-page .practice-card { padding: 28px; margin-top: 28px; }
        .golf-tips-page .practice-card h2 { font-size: 1.6rem; margin-bottom: 12px; }
        .golf-tips-page ol.practice { margin: 0; padding-left: 1.3rem; color: var(--muted); }
        .golf-tips-page ol.practice li { margin: 8px 0; }
        .golf-tips-page .footer-note { color: var(--muted); font-size: 0.92rem; margin-top: 24px; }
        @media (max-width: 820px) { .golf-tips-page .hero, .golf-tips-page .tips-grid { grid-template-columns: 1fr; } .golf-tips-main { padding-top: 28px; } .golf-tips-page .hero-card { padding: 26px; } }
      `}</style>
      <main className="golf-tips-main">
        <section className="hero" aria-labelledby="page-title">
          <div className="hero-card">
            <p className="eyebrow">Living golf checklist</p>
            <h1 id="page-title">Cesar’s Top 10 Golf Tips</h1>
            <p className="dek">These are the highest-leverage cues from your recent lessons and practice notes. Use them as a short diagnostic page, not as ten swing thoughts at once. Start with setup, then pick the one or two cues that restore solid middle-of-face contact.</p>
          </div>
          <aside className="update-card" aria-label="Version and current focus">
            <div className="focus">
              <div>Current priority</div>
              <ul className="priority-list">
                <li>Keep knees bent/flexed in the backswing.</li>
                <li>Use the same shoulder-width stance for irons and driver.</li>
                <li>Keep head behind the ball.</li>
              </ul>
            </div>
          </aside>
        </section>

        <section className="tips-grid" aria-label="Top 10 golf tips">
          {tips.map((tip) => (
            <article className="tip" key={tip.number}>
              <div className="tip-header">
                <span className="num">{tip.number}</span>
                <h2>{tip.title}</h2>
              </div>
              <div className="cue">{tip.cue}</div>
              <p>{tip.body}</p>
            </article>
          ))}
        </section>

        <section className="practice-card" aria-labelledby="practice-title">
          <h2 id="practice-title">Quick reset sequence</h2>
          <ol className="practice">
            {resetSteps.map((step) => <li key={step}>{step}</li>)}
          </ol>
        </section>

        <p className="footer-note">Maintenance note: this page should be treated as a versioned living page. When Cesar’s top 10 changes, update the version date, reorder or replace the affected tips, and keep the reset sequence aligned with the latest lesson notes.</p>
      </main>
    </div>
  );
}
